import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react'
import { useSocket } from './SocketContext'
import { useAuthStore } from '../services/auth'
import BidNotificationStack from '../components/BidNotificationStack'
import { socketDebug } from '../utils/socketDebug'

interface BidNotificationData {
  itemId: string
  bid: {
    id: string
    user: {
      id: string
      displayName: string
      profileImageUrl?: string
    }
    item: {
      id: string
      title: string
      images?: string[]
      imageUrl?: string
      mainImageIndex?: number
    }
  }
  amount: number
  userId: string
  item: {
    id: string
    currentPrice: number
    startPrice: number
  }
}

interface StackedNotification extends BidNotificationData {
  id: string
  createdAt: number
}

interface BidNotificationContextType {
  showNotification: (data: BidNotificationData) => void
  clearNotification: (id: string) => void
  notifications: StackedNotification[]
  testNotification: () => void
}

const BidNotificationContext = createContext<BidNotificationContextType>({
  showNotification: () => {},
  clearNotification: () => {},
  notifications: [],
  testNotification: () => {},
})

export const useBidNotification = () => {
  const context = useContext(BidNotificationContext)
  if (!context) {
    throw new Error('useBidNotification must be used within a BidNotificationProvider')
  }
  return context
}

interface BidNotificationProviderProps {
  children: ReactNode
}

export const BidNotificationProvider: React.FC<BidNotificationProviderProps> = ({ children }) => {
  const [notifications, setNotifications] = useState<StackedNotification[]>([])
  const { socket } = useSocket()
  const { user } = useAuthStore()

  const showNotification = (data: BidNotificationData) => {
    // Show notification called
    
    // Don't show notification for the user's own bids
    if (user && data.userId === user.id) {
      // Blocking notification - user's own bid
      return
    }

    // Create a new stacked notification
    const stackedNotification: StackedNotification = {
      ...data,
      id: `notification-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      createdAt: Date.now()
    }

    // Add to the stack (newest at the end, so it appears at bottom)
    setNotifications(prev => {
      // Limit to maximum 4 notifications
      const newNotifications = [...prev, stackedNotification]
      const result = newNotifications.slice(-4) // Keep only the last 4
      // Added notification to stack
      return result
    })

    // Auto-remove after 30 seconds
    setTimeout(() => {
      clearNotification(stackedNotification.id)
    }, 30000)
  }

  const clearNotification = (id: string) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id))
  }

  // Listen for bid events from socket
  useEffect(() => {
    if (!socket) {
      socketDebug.log('❌ No socket available for bid notifications')
      return
    }

    socketDebug.log('🔌 Setting up bid notification listeners')
    socketDebug.log('Socket connected:', socket.connected)
    socketDebug.log('Socket ID:', socket.id)

    const handleBidPlaced = (data: BidNotificationData) => {
      // Received bid notification
      socketDebug.log('🔔 BidNotificationProvider received bidPlaced event:', data)
      showNotification(data)
    }

    const handleBidPlacedAlt = (data: BidNotificationData) => {
      // Received bid-placed notification
      socketDebug.log('🔔 BidNotificationProvider received bid-placed event:', data)
      showNotification(data)
    }

    // Listen to both bid event types
    socket.on('bidPlaced', handleBidPlaced)
    socket.on('bid-placed', handleBidPlacedAlt)

    // Set up socket debugging
    const cleanupDebug = socketDebug.testSocketConnection(socket, user)

    return () => {
      socket.off('bidPlaced', handleBidPlaced)
      socket.off('bid-placed', handleBidPlacedAlt)
      if (cleanupDebug) cleanupDebug()
    }
  }, [socket, user])

  const testNotification = () => {
    socketDebug.testNotificationSystem(showNotification)
  }

  const value = {
    showNotification,
    clearNotification,
    notifications,
    testNotification,
  }

  return (
    <BidNotificationContext.Provider value={value}>
      {children}
      <BidNotificationStack
        notifications={notifications}
        onClose={clearNotification}
      />
    </BidNotificationContext.Provider>
  )
}

export default BidNotificationProvider
